import { useEffect, useState, type FormEvent } from 'react';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { useCreateAccount, useUpdateAccount, type SaveAccountInput } from '@/hooks/useAccounting';
import {
  ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS, ACCOUNT_RANGES,
  type Account, type AccountType,
} from '@/types/accounting';

type Props = {
  open: boolean;
  onClose: () => void;
  account?: Account | null;
};

const EMPTY: SaveAccountInput = {
  number: '',
  name: '',
  type: 'asset',
  subtype: '',
  description: '',
  active: true,
};

export function AccountFormModal({ open, onClose, account }: Props) {
  const create = useCreateAccount();
  const update = useUpdateAccount();
  const editing = !!account;

  const [form, setForm] = useState<SaveAccountInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(account ? hydrate(account) : EMPTY);
      setError(null);
    }
  }, [open, account]);

  function update_<K extends keyof SaveAccountInput>(key: K, val: SaveAccountInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing && account) {
        await update.mutateAsync({ id: account.id, ...form });
        toast.success(`Updated ${form.name}`);
      } else {
        await create.mutateAsync(form);
        toast.success(`Created ${form.number} ${form.name}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const [rangeStart, rangeEnd] = ACCOUNT_RANGES[form.type];

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Account' : 'New Account'}
      title={editing ? `${account?.number} · ${account?.name}` : 'Add to chart of accounts.'}
      description={editing ? undefined : 'Create a new ledger account. The number must fall within the type\'s range.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="acct-form" loading={submitting} leftIcon={<BookOpen size={13} />}>
            {editing ? 'Save' : 'Create Account'}
          </Button>
        </>
      }
    >
      <form id="acct-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Account Number"
            required
            placeholder={String(rangeStart)}
            value={form.number}
            onChange={(e) => update_('number', e.target.value.replace(/[^\d.]/g, ''))}
            hint={`${ACCOUNT_TYPE_LABELS[form.type]} range: ${rangeStart}–${rangeEnd}`}
          />
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => update_('type', e.target.value as AccountType)}
            options={ACCOUNT_TYPES.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABELS[t] }))}
          />
        </div>

        <Input
          label="Name"
          required
          placeholder="Cash · Operating"
          value={form.name}
          onChange={(e) => update_('name', e.target.value)}
        />

        <Input
          label="Subtype (optional)"
          placeholder="e.g. Current Asset, COGS, Operating Expense"
          value={form.subtype ?? ''}
          onChange={(e) => update_('subtype', e.target.value)}
        />

        <Textarea
          label="Description"
          rows={2}
          value={form.description ?? ''}
          onChange={(e) => update_('description', e.target.value)}
        />

        {editing && (
          <div className="pt-3 border-t border-border">
            <Switch
              checked={form.active}
              onChange={(v) => update_('active', v)}
              label="Active"
              description="Inactive accounts cannot be used in new journal entries but retain history."
            />
          </div>
        )}

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}

function hydrate(a: Account): SaveAccountInput {
  return {
    number: a.number, name: a.name, type: a.type,
    subtype: a.subtype ?? '', description: a.description ?? '',
    parentId: a.parentId, active: a.active,
  };
}
